// src/pages/AdminDashboard.js - UPDATED FOR SEPARATE PICKUP/DELIVERY LINKS
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  updateDoc, 
  doc,
  serverTimestamp,
  getDoc,
  where 
} from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import { generateBOL } from '../services/bolService';
import { notifyBOLGenerated } from '../services/emailService';
import SignatureCapture from '../components/SignatureCapture';

// Function to generate display ID (same as in Dashboard)
const generateDisplayId = (firebaseId, index) => {
  if (firebaseId && firebaseId.includes('_')) {
    const parts = firebaseId.split('_');
    if (parts.length >= 3) {
      const timestamp = parts[1];
      const randomPart = parts[2];
      return `SH${timestamp.slice(-6)}${randomPart.substring(0, 4).toUpperCase()}`;
    }
  }
  return `SH${String(index + 1).padStart(6, '0')}`;
};

const AdminDashboard = () => {
  const [shipments, setShipments] = useState([]);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [generatingBOL, setGeneratingBOL] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [selectedShipmentForSignature, setSelectedShipmentForSignature] = useState(null);
  const [signatureType, setSignatureType] = useState('driver');
  const [activeTab, setActiveTab] = useState('shipments'); // 'shipments' or 'quotes'
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    pendingVerification: 0,
    inTransit: 0,
    delivered: 0,
    quoteRequests: 0,
    hiddenShipments: 0
  });
  const { currentUser, isAdmin } = useAuth();

  useEffect(() => {
    if (!currentUser) {
      console.log('No user authenticated');
      return;
    }

    if (!isAdmin) {
      console.log('User is not admin:', currentUser.uid);
      return;
    }

    console.log('Admin user authenticated:', currentUser.uid);

    // Listen to ALL shipments but track which ones are hidden from customers
    const shipmentsQuery = query(
      collection(db, 'shipments'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeShipments = onSnapshot(shipmentsQuery, (querySnapshot) => {
      const shipmentsData = [];
      let pendingCount = 0;
      let pendingVerificationCount = 0;
      let inTransitCount = 0;
      let deliveredCount = 0;
      let hiddenCount = 0;

      querySnapshot.forEach((doc, index) => {
        const shipment = { 
          id: doc.id, 
          ...doc.data(),
          displayId: generateDisplayId(doc.id, index),
          createdAt: doc.data().createdAt?.toDate?.() || new Date()
        };
        shipmentsData.push(shipment);

        // Count hidden shipments (not shown in customer dashboard)
        if (!shipment.shouldShowInDashboard) {
          hiddenCount++;
        }

        if (shipment.status === 'pending_verification') {
          pendingVerificationCount++;
        } else if (shipment.status === 'pending' || shipment.status === 'pending_payment' || shipment.status === 'awaiting_payment') {
          pendingCount++;
        } else if (shipment.status === 'in_transit') {
          inTransitCount++;
        } else if (shipment.status === 'delivered') {
          deliveredCount++;
        }
      });

      setShipments(shipmentsData);
      setStats(prev => ({
        ...prev,
        total: shipmentsData.length,
        pending: pendingCount,
        pendingVerification: pendingVerificationCount,
        inTransit: inTransitCount,
        delivered: deliveredCount,
        hiddenShipments: hiddenCount
      }));
      setLoading(false);
    });

    // Listen to quote requests
    const quotesQuery = query(
      collection(db, 'quoteRequests'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeQuotes = onSnapshot(quotesQuery, (querySnapshot) => {
      const quotesData = [];
      querySnapshot.forEach((doc) => {
        quotesData.push({ 
          id: doc.id, 
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date()
        });
      });

      setQuoteRequests(quotesData);
      setStats(prev => ({
        ...prev,
        quoteRequests: quotesData.length
      }));
    });

    return () => {
      unsubscribeShipments();
      unsubscribeQuotes();
    };
  }, [currentUser, isAdmin]);

  // Function to manually show/hide shipment in customer dashboard
  const handleToggleDashboardVisibility = async (shipmentId, shouldShow) => {
    try {
      await updateDoc(doc(db, 'shipments', shipmentId), {
        shouldShowInDashboard: shouldShow,
        updatedAt: serverTimestamp()
      });
      
      alert(`Shipment ${shouldShow ? 'shown' : 'hidden'} in customer dashboard`);
    } catch (error) {
      console.error('Error updating dashboard visibility:', error);
      alert('Error updating dashboard visibility');
    }
  };

  // ✅ NEW: Generate driver links for pickup and delivery
  const generateDriverLink = async (shipmentId, step) => {
    try {
      const shipmentDoc = await getDoc(doc(db, 'shipments', shipmentId));
      if (!shipmentDoc.exists()) {
        alert('Shipment not found');
        return null;
      }

      const shipment = shipmentDoc.data();
      
      if (!shipment.assignedDriver) {
        alert('No driver assigned to this shipment');
        return null;
      }

      // Generate unique token for this step
      const token = `token_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const driverId = shipment.assignedDriver.driverId;
      
      // Generate the link
      const baseUrl = window.location.origin;
      const driverLink = `${baseUrl}/driver-signature/${shipmentId}?token=${token}&driverId=${driverId}&step=${step}`;

      // Update the shipment with the appropriate token
      const updateData = {
        updatedAt: serverTimestamp(),
        [`assignedDriver.${step}Token`]: token
      };

      await updateDoc(doc(db, 'shipments', shipmentId), updateData);

      return driverLink;
    } catch (error) {
      console.error('Error generating driver link:', error);
      alert('Error generating driver link');
      return null;
    }
  };

  // ✅ NEW: Send pickup link to driver
  const handleSendPickupLink = async (shipmentId) => {
    try {
      const pickupLink = await generateDriverLink(shipmentId, 'pickup');
      if (pickupLink) {
        // Copy to clipboard
        navigator.clipboard.writeText(pickupLink).then(() => {
          alert('✅ Pickup link generated and copied to clipboard! Send this to the driver when they are at the pickup location.');
        }).catch(() => {
          alert(`✅ Pickup link generated! Copy this link manually:\n\n${pickupLink}`);
        });
      }
    } catch (error) {
      console.error('Error sending pickup link:', error);
      alert('Error sending pickup link');
    }
  };

  // ✅ NEW: Send delivery link to driver
  const handleSendDeliveryLink = async (shipmentId) => {
    try {
      const deliveryLink = await generateDriverLink(shipmentId, 'delivery');
      if (deliveryLink) {
        // Copy to clipboard
        navigator.clipboard.writeText(deliveryLink).then(() => {
          alert('✅ Delivery link generated and copied to clipboard! Send this to the driver when they are at the delivery location.');
        }).catch(() => {
          alert(`✅ Delivery link generated! Copy this link manually:\n\n${deliveryLink}`);
        });
      }
    } catch (error) {
      console.error('Error sending delivery link:', error);
      alert('Error sending delivery link');
    }
  };

  const handleStatusUpdate = async (shipmentId, newStatus) => {
    try {
      const updateData = {
        status: newStatus,
        updatedAt: serverTimestamp()
      };
      
      // Auto-show in dashboard when payment is verified or status changes to confirmed/in_transit
      if (newStatus === 'confirmed' || newStatus === 'in_transit' || newStatus === 'delivered') {
        updateData.shouldShowInDashboard = true;
      }
      
      if (newStatus === 'delivered') {
        updateData.customerCanViewBOL = true;
        updateData.deliveredAt = serverTimestamp();
        
        const shipment = shipments.find(s => s.id === shipmentId);
        if (shipment && hasBothSignatures(shipment)) {
          setTimeout(() => {
            handleGenerateBOL(shipmentId);
          }, 1000);
        }
      }
      
      await updateDoc(doc(db, 'shipments', shipmentId), updateData);
      alert(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status');
    }
  };

  const handlePaymentVerification = async (shipmentId, verified) => {
    try {
      const newStatus = verified ? 'confirmed' : 'pending_verification';
      const updateData = {
        status: newStatus,
        paymentVerified: verified,
        paymentVerifiedAt: verified ? serverTimestamp() : null,
        paymentVerifiedBy: verified ? currentUser.uid : null,
        updatedAt: serverTimestamp()
      };
      
      // Auto-show in dashboard when payment is verified
      if (verified) {
        updateData.shouldShowInDashboard = true;
      }
      
      await updateDoc(doc(db, 'shipments', shipmentId), updateData);
      
      alert(verified ? 'Payment verified successfully!' : 'Payment verification removed.');
    } catch (error) {
      console.error('Error verifying payment:', error);
      alert('Error verifying payment');
    }
  };

  const handleGenerateBOL = async (shipmentId) => {
    let shipment;
    
    try {
      if (!currentUser) {
        alert('You must be logged in to generate BOL');
        return;
      }

      if (!isAdmin) {
        alert('Admin privileges required to generate BOL');
        return;
      }

      setGeneratingBOL(shipmentId);

      const shipmentDoc = await getDoc(doc(db, 'shipments', shipmentId));
      if (!shipmentDoc.exists()) {
        alert('Shipment not found');
        return;
      }

      shipment = { id: shipmentDoc.id, ...shipmentDoc.data() };
      
      if (!shipment.id || !shipment.invoiceNumber) {
        alert('Shipment is missing required data (ID or Invoice Number). Please check the shipment details.');
        return;
      }

      console.log('Starting BOL generation process for shipment:', {
        id: shipment.id,
        invoiceNumber: shipment.invoiceNumber,
        name: shipment.userName,
        currentUser: currentUser.uid,
        hasDriverSignature: !!shipment.signatures?.driver?.signature,
        hasCustomerSignature: !!shipment.signatures?.customer?.signature,
        shouldShowInDashboard: shipment.shouldShowInDashboard
      });
      
      const bolUrl = await generateBOL(shipment, {}, currentUser);
      
      try {
        await notifyBOLGenerated(shipmentId);
        console.log('BOL notification sent successfully');
      } catch (notificationError) {
        console.log('BOL generated but notification failed:', notificationError);
      }
      
      alert('Professional BOL generated successfully!');
      
    } catch (error) {
      console.error('Error generating BOL:', error);
      alert('Error generating BOL: ' + error.message);
    } finally {
      setGeneratingBOL(null);
    }
  };

  const handleCaptureSignature = (shipmentId, type) => {
    setSelectedShipmentForSignature(shipmentId);
    setSignatureType(type);
    setShowSignatureModal(true);
  };

  const handleSignatureSave = async () => {
    setShowSignatureModal(false);
    
    const shipment = shipments.find(s => s.id === selectedShipmentForSignature);
    if (shipment) {
      try {
        if (hasBothSignatures(shipment)) {
          setTimeout(() => {
            handleGenerateBOL(selectedShipmentForSignature);
          }, 500);
        } else {
          alert('Signature saved successfully! Generate BOL to include the signature.');
        }
      } catch (error) {
        console.error('Error updating BOL with signature:', error);
        alert('Signature saved but there was an error updating the BOL. Please regenerate the BOL.');
      }
    }
  };

  const downloadBOL = async (shipmentId) => {
    const shipment = shipments.find(s => s.id === shipmentId);
    if (shipment?.bolUrl) {
      window.open(shipment.bolUrl, '_blank');
    } else {
      alert('BOL not yet generated for this shipment');
    }
  };

  const viewReceiptDetails = async (shipmentId) => {
    try {
      const shipment = shipments.find(s => s.id === shipmentId);
      if (!shipment) {
        alert('Shipment not found');
        return;
      }
      
      setSelectedShipment(shipment);
      setIsReceiptModalOpen(true);
    } catch (error) {
      console.error('Error loading shipment details:', error);
      alert('Error loading shipment details');
    }
  };

  const closeReceiptModal = () => {
    setIsReceiptModalOpen(false);
    setSelectedShipment(null);
  };

  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await updateDoc(doc(db, 'quoteRequests', quoteId), {
        status: status,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      });
      alert(`Quote request marked as ${status}`);
    } catch (error) {
      console.error('Error updating quote status:', error);
      alert('Error updating quote status');
    }
  };

  // Helper functions
  const hasBothSignatures = (shipment) => {
    return shipment.signatures?.driver?.signature && shipment.signatures?.customer?.signature;
  };

  const hasSignatures = (shipment) => {
    return shipment.signatures?.driver?.signature || shipment.signatures?.customer?.signature;
  };

  const getSignatureStatus = (shipment) => {
    const driverSigned = shipment.signatures?.delivery_driver?.signature;
    const customerSigned = shipment.signatures?.delivery_customer?.signature || shipment.signatures?.delivery_customer?.skipped;
    
    if (driverSigned && customerSigned) return 'Both Signed';
    if (driverSigned) return 'Driver Signed';
    if (customerSigned) return 'Customer Signed';
    return 'No Signatures';
  };

  // ✅ UPDATED: Check if driver documentation is completed
  const getDriverDocumentationStatus = (shipment) => {
    const hasPickupPhotos = shipment.signatures?.pickup_photos?.length > 0;
    const hasDeliveryPhotos = shipment.signatures?.delivery_photos?.length > 0;
    const hasDriverSignature = shipment.signatures?.delivery_driver?.signature;
    const hasCustomerSignature = shipment.signatures?.delivery_customer?.signature || shipment.signatures?.delivery_customer?.skipped;

    if (hasPickupPhotos && hasDeliveryPhotos && hasDriverSignature && hasCustomerSignature) {
      return 'Completed';
    } else if (hasPickupPhotos && !hasDeliveryPhotos) {
      return 'Pickup Done';
    } else if (hasPickupPhotos && hasDeliveryPhotos && !hasDriverSignature) {
      return 'Delivery Photos Only';
    } else {
      return 'Not Started';
    }
  };

  const canGenerateBOL = (shipment) => {
    return shipment.paymentVerified || shipment.paymentMethod === 'Stripe';
  };

  // Get dashboard visibility status
  const getDashboardVisibility = (shipment) => {
    return shipment.shouldShowInDashboard ? 'Visible' : 'Hidden';
  };

  // Get dashboard visibility badge
  const getVisibilityBadge = (shipment) => {
    const isVisible = shipment.shouldShowInDashboard;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
        isVisible ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
      }`}>
        {isVisible ? 'Visible to Customer' : 'Hidden from Customer'}
      </span>
    );
  };

  // ✅ NEW: Get driver documentation badge
  const getDriverDocumentationBadge = (shipment) => {
    const status = getDriverDocumentationStatus(shipment);
    const config = {
      'Completed': { color: 'bg-green-100 text-green-800', label: '✅ Complete' },
      'Pickup Done': { color: 'bg-blue-100 text-blue-800', label: '📸 Pickup Done' },
      'Delivery Photos Only': { color: 'bg-yellow-100 text-yellow-800', label: '📷 Delivery Photos' },
      'Not Started': { color: 'bg-gray-100 text-gray-800', label: '⏳ Not Started' }
    };

    const badgeConfig = config[status] || config['Not Started'];
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badgeConfig.color}`}>
        {badgeConfig.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      pending_payment: { color: 'bg-orange-100 text-orange-800', label: 'Pending Payment' },
      awaiting_payment: { color: 'bg-orange-100 text-orange-800', label: 'Awaiting Payment' },
      pending_verification: { color: 'bg-red-100 text-red-800', label: 'Verify Payment' },
      confirmed: { color: 'bg-blue-100 text-blue-800', label: 'Confirmed' },
      in_transit: { color: 'bg-purple-100 text-purple-800', label: 'In Transit' },
      delivered: { color: 'bg-green-100 text-green-800', label: 'Delivered' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getQuoteStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      contacted: { color: 'bg-blue-100 text-blue-800', label: 'Contacted' },
      quoted: { color: 'bg-purple-100 text-purple-800', label: 'Quoted' },
      converted: { color: 'bg-green-100 text-green-800', label: 'Converted' },
      rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Authentication Required</h2>
          <p className="text-gray-600">Please log in to access the admin dashboard.</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">Admin privileges required to access this dashboard.</p>
          <p className="text-sm text-gray-500 mt-2">Current user: {currentUser.email}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600">Manage all shipments and quote requests</p>
          <p className="text-sm text-green-600 mt-1">
            Logged in as: {currentUser.email} (Admin)
          </p>
          {/* Hidden shipments notice */}
          <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> {stats.hiddenShipments} shipments are currently hidden from customer dashboard (pending payment/verification).
              Admins can see all shipments here.
            </p>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-7 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-gray-900">Total Shipments</h3>
                <p className="text-xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-yellow-100 text-yellow-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-gray-900">Pending</h3>
                <p className="text-xl font-bold text-gray-900">{stats.pending}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-red-100 text-red-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-gray-900">Verify Payment</h3>
                <p className="text-xl font-bold text-gray-900">{stats.pendingVerification}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-purple-100 text-purple-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-gray-900">In Transit</h3>
                <p className="text-xl font-bold text-gray-900">{stats.inTransit}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-green-100 text-green-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-gray-900">Delivered</h3>
                <p className="text-xl font-bold text-gray-900">{stats.delivered}</p>
              </div>
            </div>
          </div>

          {/* Hidden shipments stat */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-gray-100 text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-gray-900">Hidden</h3>
                <p className="text-xl font-bold text-gray-900">{stats.hiddenShipments}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-indigo-100 text-indigo-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-gray-900">Quote Requests</h3>
                <p className="text-xl font-bold text-gray-900">{stats.quoteRequests}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('shipments')}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'shipments'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Shipments ({shipments.length})
              </button>
              <button
                onClick={() => setActiveTab('quotes')}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'quotes'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Quote Requests ({quoteRequests.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Shipments Tab */}
        {activeTab === 'shipments' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">All Shipments ({shipments.length})</h2>
              <p className="text-sm text-gray-600 mt-1">
                Showing all shipments including {stats.hiddenShipments} hidden from customer dashboard
              </p>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shipment ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Method</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dashboard</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver Docs</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Signatures</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {shipments.map((shipment) => (
                      <tr key={shipment.id} className={`hover:bg-gray-50 ${
                        !shipment.shouldShowInDashboard ? 'bg-yellow-50' : ''
                      }`}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900 font-mono">
                              {shipment.displayId}
                            </div>
                            <div className="text-xs text-gray-500">
                              {shipment.id.substring(0, 16)}...
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {shipment.userName}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {shipment.itemDescription}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {shipment.paymentMethod}
                          {shipment.paymentVerified && (
                            <span className="ml-1 text-green-600" title="Payment Verified">✓</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {getStatusBadge(shipment.status)}
                          <select
                            value={shipment.status}
                            onChange={(e) => handleStatusUpdate(shipment.id, e.target.value)}
                            className="text-xs border rounded p-1 mt-1 w-full"
                          >
                            <option value="pending">Pending</option>
                            <option value="pending_payment">Pending Payment</option>
                            <option value="awaiting_payment">Awaiting Payment</option>
                            <option value="pending_verification">Pending Verification</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="in_transit">In Transit</option>
                            <option value="delivered">Delivered</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {getVisibilityBadge(shipment)}
                          <div className="mt-1 flex gap-1">
                            <button
                              onClick={() => handleToggleDashboardVisibility(shipment.id, !shipment.shouldShowInDashboard)}
                              className={`text-xs px-2 py-1 border rounded ${
                                shipment.shouldShowInDashboard 
                                  ? 'text-red-600 border-red-300 hover:bg-red-50' 
                                  : 'text-green-600 border-green-300 hover:bg-green-50'
                              }`}
                            >
                              {shipment.shouldShowInDashboard ? 'Hide' : 'Show'}
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {getDriverDocumentationBadge(shipment)}
                          {shipment.assignedDriver && (
                            <div className="mt-1 flex flex-col gap-1">
                              <button
                                onClick={() => handleSendPickupLink(shipment.id)}
                                className="text-xs px-2 py-1 border border-blue-300 text-blue-600 rounded hover:bg-blue-50"
                              >
                                Send Pickup Link
                              </button>
                              <button
                                onClick={() => handleSendDeliveryLink(shipment.id)}
                                className="text-xs px-2 py-1 border border-green-300 text-green-600 rounded hover:bg-green-50"
                              >
                                Send Delivery Link
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            hasBothSignatures(shipment) 
                              ? 'bg-green-100 text-green-800' 
                              : hasSignatures(shipment)
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {getSignatureStatus(shipment)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {shipment.invoiceNumber || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium space-y-1">
                          <div className="flex flex-wrap gap-1">
                            <button
                              onClick={() => viewReceiptDetails(shipment.id)}
                              className="text-blue-600 hover:text-blue-900 text-xs px-2 py-1 border border-blue-300 rounded"
                            >
                              View Details
                            </button>
                            
                            {shipment.paymentMethod !== 'Stripe' && shipment.status === 'pending_verification' && (
                              <button
                                onClick={() => handlePaymentVerification(shipment.id, true)}
                                className="text-green-600 hover:text-green-900 text-xs px-2 py-1 border border-green-300 rounded"
                              >
                                Verify Payment
                              </button>
                            )}
                            
                            {!shipment.bolUrl ? (
                              <button
                                onClick={() => handleGenerateBOL(shipment.id)}
                                disabled={!canGenerateBOL(shipment) || generatingBOL === shipment.id}
                                className="text-blue-600 hover:text-blue-900 text-xs px-2 py-1 border border-blue-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                title={!canGenerateBOL(shipment) ? 'Verify payment first' : 'Generate BOL'}
                              >
                                {generatingBOL === shipment.id ? 'Generating...' : 'Generate BOL'}
                              </button>
                            ) : (
                              <button
                                onClick={() => downloadBOL(shipment.id)}
                                className="text-green-600 hover:text-green-900 text-xs px-2 py-1 border border-green-300 rounded"
                              >
                                View BOL
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Quote Requests Tab - No changes needed here */}
        {activeTab === 'quotes' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Quote Requests ({quoteRequests.length})</h2>
            </div>
            <div className="p-6">
              {quoteRequests.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No quote requests</h3>
                  <p className="mt-1 text-sm text-gray-500">New quote requests will appear here.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {quoteRequests.map((request) => (
                    <div key={request.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{request.name}</h3>
                          <p className="text-sm text-gray-600">
                            {request.email} | {request.phone || 'No phone'} | {request.company || 'No company'}
                          </p>
                        </div>
                        <div className="text-right">
                          {getQuoteStatusBadge(request.status)}
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDate(request.createdAt)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm"><strong>From:</strong> {request.origin}</p>
                          <p className="text-sm"><strong>To:</strong> {request.destination}</p>
                        </div>
                        <div>
                          <p className="text-sm"><strong>Package Type:</strong> {request.packageType}</p>
                          <p className="text-sm"><strong>Weight:</strong> {request.weight || 'N/A'} kg</p>
                          <p className="text-sm"><strong>Dimensions:</strong> {request.dimensions || 'N/A'}</p>
                        </div>
                      </div>

                      {request.specialRequirements && (
                        <div className="mb-4">
                          <p className="text-sm"><strong>Special Requirements:</strong></p>
                          <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">{request.specialRequirements}</p>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => updateQuoteStatus(request.id, 'contacted')}
                          className="text-blue-600 hover:text-blue-900 text-xs px-3 py-1 border border-blue-300 rounded"
                        >
                          Mark as Contacted
                        </button>
                        <button
                          onClick={() => updateQuoteStatus(request.id, 'quoted')}
                          className="text-purple-600 hover:text-purple-900 text-xs px-3 py-1 border border-purple-300 rounded"
                        >
                          Mark as Quoted
                        </button>
                        <button
                          onClick={() => updateQuoteStatus(request.id, 'converted')}
                          className="text-green-600 hover:text-green-900 text-xs px-3 py-1 border border-green-300 rounded"
                        >
                          Mark as Converted
                        </button>
                        <button
                          onClick={() => updateQuoteStatus(request.id, 'rejected')}
                          className="text-red-600 hover:text-red-900 text-xs px-3 py-1 border border-red-300 rounded"
                        >
                          Mark as Rejected
                        </button>
                        <a
                          href={`mailto:${request.email}?subject=Quote Request - Flex Logistics&body=Dear ${request.name},%0A%0AThank you for your quote request...`}
                          className="text-orange-600 hover:text-orange-900 text-xs px-3 py-1 border border-orange-300 rounded"
                        >
                          Send Email
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Receipt & Details Modal - Updated to show driver documentation status */}
      {isReceiptModalOpen && selectedShipment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">Shipment Receipt & Details</h3>
              <button
                onClick={closeReceiptModal}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Shipment Details */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">Shipment Information</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Display ID</label>
                      <p className="mt-1 text-sm font-mono text-gray-900 font-semibold">
                        {selectedShipment.displayId}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Firebase ID</label>
                      <p className="mt-1 text-sm font-mono text-gray-900 text-xs break-all">
                        {selectedShipment.id}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <div className="mt-1">{getStatusBadge(selectedShipment.status)}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Dashboard</label>
                      <div className="mt-1">
                        {getVisibilityBadge(selectedShipment)}
                        <button
                          onClick={() => handleToggleDashboardVisibility(selectedShipment.id, !selectedShipment.shouldShowInDashboard)}
                          className="ml-2 text-xs text-blue-600 hover:text-blue-800"
                        >
                          {selectedShipment.shouldShowInDashboard ? 'Hide' : 'Show'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* ✅ NEW: Driver Documentation Status */}
                  {selectedShipment.assignedDriver && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Driver Documentation</label>
                      <div className="mt-1">
                        {getDriverDocumentationBadge(selectedShipment)}
                        <div className="mt-2 flex gap-2">
                          <button
                            onClick={() => handleSendPickupLink(selectedShipment.id)}
                            className="text-xs px-3 py-1 border border-blue-300 text-blue-600 rounded hover:bg-blue-50"
                          >
                            Send Pickup Link
                          </button>
                          <button
                            onClick={() => handleSendDeliveryLink(selectedShipment.id)}
                            className="text-xs px-3 py-1 border border-green-300 text-green-600 rounded hover:bg-green-50"
                          >
                            Send Delivery Link
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Customer</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedShipment.userName}</p>
                    <p className="text-xs text-gray-500">{selectedShipment.userEmail}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Item Description</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedShipment.itemDescription}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Weight</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedShipment.weight} lbs</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Agreed Price</label>
                      <p className="mt-1 text-sm text-gray-900">${selectedShipment.agreedPrice}</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Pickup Address</label>
                    <p className="mt-1 text-sm text-gray-900 whitespace-pre-line">{selectedShipment.pickupAddress}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Delivery Address</label>
                    <p className="mt-1 text-sm text-gray-900 whitespace-pre-line">{selectedShipment.dropoffAddress}</p>
                  </div>

                  {selectedShipment.specialInstructions && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Special Instructions</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedShipment.specialInstructions}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Created</label>
                      <p className="mt-1 text-sm text-gray-900">{formatDate(selectedShipment.createdAt)}</p>
                    </div>
                    {selectedShipment.deliveredAt && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Delivered</label>
                        <p className="mt-1 text-sm text-gray-900">{formatDate(selectedShipment.deliveredAt)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment & Receipt Section */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">Payment Information</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Payment Method</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedShipment.paymentMethod}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Payment Verified</label>
                      <p className="mt-1 text-sm">
                        {selectedShipment.paymentVerified ? (
                          <span className="text-green-600 font-semibold">✓ Verified</span>
                        ) : (
                          <span className="text-red-600">Pending Verification</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {selectedShipment.paymentVerifiedBy && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Verified By</label>
                      <p className="mt-1 text-sm text-gray-900">Admin User</p>
                      <p className="text-xs text-gray-500">
                        {selectedShipment.paymentVerifiedAt ? formatDate(selectedShipment.paymentVerifiedAt) : 'N/A'}
                      </p>
                    </div>
                  )}

                  {/* Receipt Image */}
                  {selectedShipment.receiptUrl && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Payment Receipt</label>
                      <div className="border rounded-lg p-2 bg-gray-50">
                        <img 
                          src={selectedShipment.receiptUrl} 
                          alt="Payment Receipt" 
                          className="max-w-full max-h-64 object-contain mx-auto"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmM2YzIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPlJlY2VpcHQgTm90IEF2YWlsYWJsZTwvdGV4dD48L3N2Zz4=';
                          }}
                        />
                      </div>
                      <div className="mt-2 text-center">
                        <a
                          href={selectedShipment.receiptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Open Receipt in New Tab
                        </a>
                      </div>
                    </div>
                  )}

                  {/* BOL Status */}
                  {selectedShipment.bolUrl && (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <h5 className="font-semibold text-blue-800">Bill of Lading</h5>
                      <p className="text-sm text-blue-700 mt-1">
                        BOL has been generated and is available for download.
                      </p>
                      <a
                        href={selectedShipment.bolUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block mt-2 text-sm text-white bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded"
                      >
                        View BOL
                      </a>
                    </div>
                  )}

                  {/* Signature Status */}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h5 className="font-semibold text-gray-800">Signature Status</h5>
                    <p className="text-sm text-gray-700 mt-1">
                      {getSignatureStatus(selectedShipment)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end p-4 border-t border-gray-200">
              <button
                onClick={closeReceiptModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signature Capture Modal */}
      {showSignatureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">
                {signatureType === 'driver' ? 'Driver' : 'Customer'} Signature Capture
              </h3>
              <button
                onClick={() => setShowSignatureModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-4">
              <SignatureCapture
                shipmentId={selectedShipmentForSignature}
                type={signatureType}
                currentUser={currentUser}
                onSave={handleSignatureSave}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;